// #1028 — resolve (or lazily create) the user's default "To-Dos" list, the
// unified store's home for anything unlisted. Every general-list write path
// (chat [TODO_LIST] saves, the #682 Save-to-a-list "general" branch) routes
// through this instead of legacy notes_todos.
//
// Tolerates the pre-#1023 schema: the read selects * and feature-detects
// is_default (falling back to the list literally named "To-Dos", then to
// creating one); creation retries the legacy column shape on an error, the
// same idiom WheneverLists uses.
//
// Returns null only when even the legacy-shape create failed — callers fall
// back to the old notes_todos write in that case, which stays SAFE: the
// mergeUnifiedTodos bridge renders unmigrated legacy rows as default-list
// members everywhere, so a fail-safe legacy save is visible, not lost.

import { reportError } from "@/lib/reportError";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = any;

export async function ensureDefaultTodoList(
  client: AnyClient,
  userId: string
): Promise<{ id: string; name: string } | null> {
  const { data, error } = await client.from("todo_lists").select("*").eq("user_id", userId);
  if (error) {
    console.warn("[defaultTodoList] read failed", error);
    return null;
  }
  const lists: Array<{ id: string; name: string; is_default?: boolean }> = data || [];
  const flagged = lists.find((l) => l.is_default === true);
  if (flagged) return { id: flagged.id, name: flagged.name };
  const named = lists.find(
    (l) => typeof l.name === "string" && l.name.trim().toLowerCase() === "to-dos"
  );
  if (named) return { id: named.id, name: named.name };

  let ins = await client
    .from("todo_lists")
    .insert({ user_id: userId, name: "To-Dos", emoji: "📝", is_default: true, sort_order: 0 })
    .select("id, name")
    .single();
  if (ins.error || !ins.data) {
    reportError("[defaultTodoList] full-shape create failed; retrying legacy shape", ins.error, { tags: { area: "notes-todos" } });
    ins = await client
      .from("todo_lists")
      .insert({ user_id: userId, name: "To-Dos" })
      .select("id, name")
      .single();
  }
  if (ins.error || !ins.data) {
    reportError("[defaultTodoList] create failed", ins.error, { tags: { area: "notes-todos" } });
    return null;
  }
  return ins.data;
}
